import dayjs from "dayjs";
import type { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import type { ScrapEyesEventWithReviewData } from "../contexts/ManualReviewEventContext";
import { toLabelData } from "./formatters";
import {
  ClientIdentifiedEvent,
  LabelData,
  LabelType,
} from "src/store/api/generatedApi";

const editableLabels = {
  material: "material",
  coMaterial: "co_material",
  area: "area",
  comment: "comment",
  mass: "mass",
  initialMass: "initial_mass",
  finalMass: "final_mass",
} as const;

export type ReviewEditableLabels = Pick<
  ScrapEyesEvent,
  keyof typeof editableLabels
>;

export const withEditableReviewLabels = (
  event: ScrapEyesEventWithReviewData
): ScrapEyesEventWithReviewData =>
  event.persistedReview?.editableLabels
    ? {
        ...event,
        ...event.persistedReview.editableLabels,
        persistedReview: {
          ...event.persistedReview,
          editableLabels: undefined,
        },
      }
    : event;

export const savedEditableLabels = (
  event: ScrapEyesEventWithReviewData
): ReviewEditableLabels => ({
  material: event.material,
  coMaterial: event.coMaterial,
  area: event.area,
  comment: event.comment,
  mass: event.mass,
  initialMass: event.initialMass,
  finalMass: event.finalMass,
});

export const toClientIdentifiedReviewEvent = (
  event: ScrapEyesEventWithReviewData
): ClientIdentifiedEvent => {
  const persisted = event.persistedReview;
  const editable = withEditableReviewLabels(event);
  const updatedTypes = new Set<LabelType>(
    persisted
      ? (Object.keys(editableLabels) as (keyof typeof editableLabels)[])
          .filter(
            (field) =>
              (editable[field] ?? null) !== (persisted.baseline[field] ?? null)
          )
          .map((field) => editableLabels[field])
      : Object.values(editableLabels)
  );
  if (updatedTypes.has("material")) updatedTypes.add("co_material");
  const retained: LabelData[] = persisted
    ? persisted.contents.labels.filter(
        (label) =>
          !updatedTypes.has(label.label_type) &&
          (persisted.saved ||
            ![
              "event_confidence",
              "material_confidence",
              "area_confidence",
            ].includes(label.label_type))
      )
    : [];
  const labels = [
    ...retained,
    ...toLabelData(editable).filter((label) =>
      updatedTypes.has(label.label_type)
    ),
  ];
  const exactTimestamp = (boundary: "start" | "end"): string => {
    const original = persisted?.contents.event[boundary];
    return original !== undefined &&
      event[boundary].valueOf() === dayjs.utc(original).valueOf()
      ? original
      : event[boundary].toISOString();
  };
  return {
    client_id: event.id,
    event: {
      event_type: event.eventType,
      start: exactTimestamp("start"),
      end: exactTimestamp("end"),
    },
    labels,
  };
};
